import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Moon, Sun } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { useUiStore } from '@/stores/uiStore';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/lib/routes';

export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useUiStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleLogout() {
    logout();
    navigate(ROUTES.LOGIN, { replace: true });
  }

  const initials = user?.name
    ? user.name.slice(0, 2).toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() ?? '?';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Menu utilisateur"
        className="flex items-center gap-2"
      >
        <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-white text-xs font-bold shrink-0">
          {initials}
        </div>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-52 bg-white border border-brand-border rounded-brand shadow-lg py-1">
          <div className="px-4 py-2 border-b border-brand-border">
            <p className="text-xs font-semibold text-brand-dark truncate">{user?.name ?? user?.email}</p>
            {user?.name && (
              <p className="text-xs text-gray-400 truncate">{user.email}</p>
            )}
            {user?.role && (
              <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-brand-primary-light text-brand-primary font-medium">
                {user.role}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={toggleDarkMode}
            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {darkMode ? <Sun size={14} /> : <Moon size={14} />}
            {darkMode ? 'Mode clair' : 'Mode sombre'}
          </button>

          <div className="border-t border-brand-border mt-1 pt-1">
            <button
              type="button"
              onClick={handleLogout}
              className={cn(
                'flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors',
              )}
            >
              <LogOut size={14} />
              Déconnexion
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
